import React from 'react';
import { fichaFieldsBySection } from '../../data/fichaFields';

const QUESTION_TYPES = [
    { value: 'text', label: 'Texto libre' },
    { value: 'boolean', label: 'Sí / No' },
    { value: 'choice', label: 'Opción múltiple' },
    { value: 'date', label: 'Fecha' },
    { value: 'number', label: 'Número' },
    { value: 'file_upload', label: 'Subir archivo' },
];

const QuestionEditor = ({ question, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) => {

    const update = (field, value) => {
        onChange(index, { ...question, [field]: value });
    };

    const updateValidation = (field, value) => {
        onChange(index, {
            ...question,
            validation: { ...question.validation, [field]: value }
        });
    };

    const addOption = () => {
        const options = question.options || [];
        const nextIdx = options.length + 1;
        onChange(index, {
            ...question,
            options: [...options, { value: `opt_${nextIdx}`, label: '', synonyms: [] }]
        });
    };

    const updateOption = (optIdx, field, value) => {
        const options = [...(question.options || [])];
        options[optIdx] = { ...options[optIdx], [field]: value };
        onChange(index, { ...question, options });
    };

    const removeOption = (optIdx) => {
        const options = (question.options || []).filter((_, i) => i !== optIdx);
        onChange(index, { ...question, options });
    };

    return (
        <div className="question-editor">
            <div className="question-editor-header">
                <span className="question-number">P{index + 1}</span>
                <div className="question-actions">
                    <button type="button" onClick={() => onMoveUp(index)} disabled={isFirst} className="btn-icon" title="Subir">↑</button>
                    <button type="button" onClick={() => onMoveDown(index)} disabled={isLast} className="btn-icon" title="Bajar">↓</button>
                    <button type="button" onClick={() => onRemove(index)} className="btn-icon btn-danger" title="Eliminar">✕</button>
                </div>
            </div>

            <div className="question-editor-body">
                <div className="form-row">
                    <div className="form-group flex-2">
                        <label>ID de pregunta</label>
                        <input
                            type="text"
                            value={question.id || ''}
                            onChange={(e) => update('id', e.target.value.replace(/\s/g, '_').toLowerCase())}
                            placeholder="ej: q_nombre_paciente"
                        />
                    </div>
                    <div className="form-group flex-1">
                        <label>Tipo</label>
                        <select value={question.type || 'text'} onChange={(e) => update('type', e.target.value)}>
                            {QUESTION_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label>Texto de la pregunta</label>
                    <textarea
                        value={question.text || ''}
                        onChange={(e) => update('text', e.target.value)}
                        placeholder="¿Cuál es tu nombre completo?"
                        rows={2}
                    />
                </div>

                {/* Options for choice type */}
                {question.type === 'choice' && (
                    <div className="options-section">
                        <label>Opciones</label>
                        {(question.options || []).map((opt, optIdx) => (
                            <div key={optIdx} className="option-row">
                                <input
                                    type="text"
                                    value={opt.value}
                                    onChange={(e) => updateOption(optIdx, 'value', e.target.value.replace(/\s/g, '_').toLowerCase())}
                                    placeholder="Valor"
                                    className="option-value"
                                />
                                <input
                                    type="text"
                                    value={opt.label}
                                    onChange={(e) => updateOption(optIdx, 'label', e.target.value)}
                                    placeholder="Etiqueta visible"
                                    className="option-label"
                                />
                                <input
                                    type="text"
                                    value={(opt.synonyms || []).join(', ')}
                                    onChange={(e) => updateOption(optIdx, 'synonyms', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                    placeholder="Sinónimos (sep. por coma)"
                                    className="option-synonyms"
                                />
                                <button type="button" onClick={() => removeOption(optIdx)} className="btn-icon btn-danger">✕</button>
                            </div>
                        ))}
                        <button type="button" onClick={addOption} className="btn-secondary btn-sm">+ Agregar opción</button>
                    </div>
                )}

                {/* Validation rules */}
                <div className="form-row validation-row">
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                checked={question.validation?.required !== false}
                                onChange={(e) => updateValidation('required', e.target.checked)}
                            />
                            {' '}Obligatoria
                        </label>
                    </div>
                    {(question.type === 'text') && (
                        <>
                            <div className="form-group">
                                <label>Mín. caracteres</label>
                                <input type="number" value={question.validation?.min_length || ''} onChange={(e) => updateValidation('min_length', e.target.value ? parseInt(e.target.value) : null)} />
                            </div>
                            <div className="form-group">
                                <label>Máx. caracteres</label>
                                <input type="number" value={question.validation?.max_length || ''} onChange={(e) => updateValidation('max_length', e.target.value ? parseInt(e.target.value) : null)} />
                            </div>
                        </>
                    )}
                    {(question.type === 'number') && (
                        <>
                            <div className="form-group">
                                <label>Mín. valor</label>
                                <input type="number" value={question.validation?.min_value || ''} onChange={(e) => updateValidation('min_value', e.target.value ? parseFloat(e.target.value) : null)} />
                            </div>
                            <div className="form-group">
                                <label>Máx. valor</label>
                                <input type="number" value={question.validation?.max_value || ''} onChange={(e) => updateValidation('max_value', e.target.value ? parseFloat(e.target.value) : null)} />
                            </div>
                        </>
                    )}
                </div>

                {/* Mapper: maps_to ficha field */}
                <div className="form-group mapper-group">
                    <label>Mapea a (Ficha Oncológica)</label>
                    <select value={question.maps_to || ''} onChange={(e) => update('maps_to', e.target.value || null)}>
                        <option value="">— Sin mapeo —</option>
                        {Object.entries(fichaFieldsBySection).map(([section, fields]) => (
                            <optgroup key={section} label={section}>
                                {fields.map(f => (
                                    <option key={f.path} value={f.path}>{f.label} ({f.path})</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </div>
            </div>

        </div>
    );
};

export default QuestionEditor;
